import type { WorkOrderFrameView, WorkOrderProgressView } from "@agentry/api-client";
import type { WorkOrder } from "@agentry/zerovia-contracts";
import { Link } from "react-router";
import { DISPLAY_TIME_ZONE, parseApiDateTime } from "../../../ui/date-time";
import { Badge, cx, StatusBadge } from "../../../ui/primitives";

export const BOARD_COLUMNS: Array<{
	status: WorkOrder["status"];
	label: string;
	tone: string;
}> = [
	{ status: "blocked", label: "裁定待ち", tone: "border-danger-500/40 bg-danger-100/40" },
	{ status: "draft", label: "下書き", tone: "border-ink-200 bg-white" },
	{ status: "issued", label: "発注済み", tone: "border-brand-300 bg-brand-50/40" },
	{ status: "implemented", label: "実装済み", tone: "border-brand-300 bg-brand-50/40" },
	{ status: "verified", label: "検証済み", tone: "border-warn-500/40 bg-warn-100/30" },
	{ status: "accepted", label: "受け入れ済み", tone: "border-accent-500/40 bg-accent-100/30" },
	{ status: "rejected", label: "差し戻し", tone: "border-danger-500/30 bg-white" },
];

export type BurnInSummary = {
	campaignId: string;
	accepted: number;
	size: number;
	status: "running" | "passed" | "failed";
	detail: string;
};

export function summarizeLatestBurnIn(
	orders: WorkOrder[],
	progress: Record<string, WorkOrderProgressView | undefined>,
): BurnInSummary | null {
	const candidates = orders.filter(
		(order) => order.kind === "implementation" && order.campaignId && order.campaignSize,
	);
	const latest = [...candidates].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
	if (!latest?.campaignId || !latest.campaignSize) return null;
	const campaign = candidates.filter((order) => order.campaignId === latest.campaignId);
	const accepted = new Set(
		campaign
			.filter((order) => order.status === "accepted")
			.map((order) => order.campaignOrdinal)
			.filter((value): value is number => value !== null),
	).size;
	const violations: string[] = [];
	for (const order of campaign) {
		const item = progress[order.id];
		if (order.attentionRequired || ["blocked", "rejected"].includes(order.status)) {
			violations.push(`${order.campaignOrdinal ?? "?"}:要対応`);
		}
		if (item?.wallClock.totalSeconds && item.wallClock.totalSeconds > 900) {
			violations.push(`${order.campaignOrdinal ?? "?"}:15分超過`);
		}
		if (item?.failureClass === "deterministic" && item.retryCount > 0) {
			violations.push(`${order.campaignOrdinal ?? "?"}:確定的retry`);
		}
		if (item?.failureClass === "transient" && item.retryCount > 1) {
			violations.push(`${order.campaignOrdinal ?? "?"}:一時的retry超過`);
		}
	}
	const status =
		violations.length > 0 ? "failed" : accepted === latest.campaignSize ? "passed" : "running";
	return {
		campaignId: latest.campaignId,
		accepted,
		size: latest.campaignSize,
		status,
		detail:
			violations.length > 0
				? violations.join(" / ")
				: status === "passed"
					? "5件連続合格・accepted時のcanonical祖先確認済み"
					: `実行中 ${campaign.length}/${latest.campaignSize}件を起票済み`,
	};
}

export function OrderCard({
	order,
	progress,
}: {
	order: WorkOrder;
	progress?: WorkOrderProgressView;
}) {
	return (
		<Link
			to={`/app/orders/${order.id}`}
			className="block rounded-xl border border-ink-200 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-500"
		>
			<div className="flex items-start justify-between gap-2">
				<h3 className="text-[13.5px] font-bold leading-snug text-ink-900">{order.title}</h3>
				<div className="flex flex-wrap justify-end gap-1">
					{order.kind === "journey" && <Badge tone="brand">ジャーニー</Badge>}
					{order.attentionRequired && <Badge tone="warning">要対応</Badge>}
				</div>
			</div>
			<p className="mt-2 truncate font-mono text-[10.5px] text-ink-500">{order.branch}</p>
			<p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-ink-400">
				{order.targetRepo}
			</p>
			{progress && <ProgressSummary progress={progress} compact />}
			{order.status === "accepted" ? (
				<p className="mt-2 text-[11.5px] text-ink-500">
					完了日時 {formatDateTime(order.updatedAt)}
				</p>
			) : !progress ? (
				<p className="mt-2 text-[11.5px] text-ink-500">
					最終更新 {formatDateTime(order.updatedAt)}
				</p>
			) : null}
		</Link>
	);
}

export function ProgressSummary({
	progress,
	compact = false,
}: {
	progress: WorkOrderProgressView;
	compact?: boolean;
}) {
	return (
		<div className={cx("mt-2 rounded-lg bg-brand-50", compact ? "p-2" : "p-3")}>
			{progress.journey && (
				<div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-md bg-white px-2 py-1.5">
					<span className="text-[11.5px] font-bold text-ink-700">
						{journeyScriptLabel(progress.journey.script)}
					</span>
					<Badge
						tone={
							progress.journey.result === "passed"
								? "success"
								: progress.journey.result === "failed"
									? "danger"
									: "warning"
						}
					>
						{journeyResultLabel(progress.journey.result)}・
						{durationLabel(progress.journey.durationSeconds)}
					</Badge>
				</div>
			)}
			<div className="flex items-center justify-between gap-2">
				<p className="flex items-center gap-1.5 text-[12px] font-bold text-brand-700">
					{progress.live && <span className="size-2 animate-pulse rounded-full bg-accent-500" />}
					{progress.label}
				</p>
				<Badge tone={progress.retryCount > 0 ? "warning" : "brand"}>
					{progress.retryCount > 0 ? `再試行 ${progress.retryCount}` : progress.phase}
				</Badge>
			</div>
			{progress.retryReason && (
				<p className="mt-1 text-[11.5px] leading-relaxed text-ink-600">{progress.retryReason}</p>
			)}
			{progress.warning && (
				<p className="mt-1 rounded-md bg-warn-100 px-2 py-1 text-[11.5px] font-semibold text-warn-700">
					警告: {progress.warning}
				</p>
			)}
			{progress.failureClass && (
				<p className="mt-1 text-[11.5px] text-danger-600">
					失敗分類: {progress.failureClass === "transient" ? "一時的" : "確定的"}
				</p>
			)}
			<div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-ink-500">
				{progress.live && <span>経過 {elapsedLabel(progress.startedAt)}</span>}
				<span>全体 {durationLabel(progress.wallClock.totalSeconds)}</span>
				<span>LLM実働 {durationLabel(progress.llmActiveSeconds)}</span>
			</div>
			{!compact && (
				<>
					<dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11.5px] text-ink-500 sm:grid-cols-3">
						{wallClockRows(progress).map(([label, seconds]) => (
							<div key={label} className="flex justify-between gap-2">
								<dt>{label}</dt>
								<dd className="font-mono text-ink-700">{durationLabel(seconds)}</dd>
							</div>
						))}
					</dl>
					<p className="mt-1.5 text-[11.5px] text-ink-500">
						開始 {formatDateTime(progress.startedAt)}
						{progress.nextRetryAt ? ` ・ 次回 ${formatDateTime(progress.nextRetryAt)}` : ""}
					</p>
				</>
			)}
		</div>
	);
}

export function journeyScriptLabel(script: string): string {
	return (
		{
			S1_first_lp: "P1 初回LP",
			S4_morning_check: "P4 朝確認",
			S5_orderer: "P5 発注者",
			S6_fault_injection: "週次障害注入",
		}[script] ?? script
	);
}

export function journeyResultLabel(result: string): string {
	return (
		{ scheduled: "予約", running: "実行中", passed: "緑", failed: "赤", skipped: "スキップ" }[
			result
		] ?? result
	);
}

function wallClockRows(progress: WorkOrderProgressView): Array<[string, number]> {
	const value = progress.wallClock;
	return [
		["待機", value.queuedSeconds],
		["起動", value.wakeSeconds],
		["準備", value.prepareSeconds],
		["LLM", value.llmSeconds],
		["CI", value.ciSeconds],
		["統合", value.integrateSeconds],
		["配信", value.deploySeconds],
		["Evidence待ち", value.evidenceWaitSeconds],
		["受入", value.acceptSeconds],
	];
}

export function Conversation({ frames }: { frames: WorkOrderFrameView[] }) {
	if (frames.length === 0) {
		return <p className="py-4 text-center text-[13px] text-ink-500">まだ会話はありません。</p>;
	}
	return (
		<ol className="max-h-72 space-y-2 overflow-y-auto pr-1" aria-label="会話履歴">
			{frames.map((frame) => (
				<li
					key={frame.id}
					className={cx(
						"max-w-[92%] rounded-xl px-3 py-2 text-[13px] leading-relaxed",
						frame.role === "user"
							? "ml-auto bg-brand-600 text-white"
							: frame.role === "system"
								? "mx-auto bg-ink-100 text-ink-600"
								: "bg-ink-50 text-ink-800",
					)}
				>
					{frame.body}
				</li>
			))}
		</ol>
	);
}

export function WorkOrderStatus({ status }: { status: WorkOrder["status"] }) {
	return <StatusBadge status={status} />;
}

export function formatDateTime(value: string): string {
	return new Intl.DateTimeFormat("ja-JP", {
		timeZone: DISPLAY_TIME_ZONE,
		month: "numeric",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(parseApiDateTime(value));
}

function elapsedLabel(startedAt: string): string {
	const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(startedAt)) / 1000));
	return durationLabel(seconds);
}

function durationLabel(seconds: number): string {
	if (seconds < 60) return `${seconds}秒`;
	const minutes = Math.floor(seconds / 60);
	const rest = seconds % 60;
	return rest > 0 ? `${minutes}分${rest}秒` : `${minutes}分`;
}
